/**
 * Root de la app. Compone providers, routing y layout.
 * El AuthProvider decide internamente entre MSAL y LocalAuth segun VITE_BACKEND.
 */

import { useEffect, useState } from "react";
import { BrowserRouter, HashRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

/**
 * Router activo según target de deploy:
 *   - SharePoint (`VITE_SP_HASH_ROUTER=1`) → `HashRouter`. SharePoint sirve
 *     archivos estáticos sin fallback a `index.html`, así que `/afe/123`
 *     daría 404. Con HashRouter las rutas viven en el fragmento (`#/afe/123`)
 *     y nunca llegan al servidor.
 *   - Dev / Vercel / Netlify → `BrowserRouter` (URLs limpias).
 */
const RouterImpl =
  (import.meta.env.VITE_SP_HASH_ROUTER as string | undefined) === "1"
    ? HashRouter
    : BrowserRouter;
import {
  FluentProvider,
  webLightTheme,
  Button,
  Spinner,
  Text,
  Toaster,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  MenuDivider,
} from "@fluentui/react-components";
import {
  ChevronDownRegular,
  PeopleSwapRegular,
  NavigationRegular,
  EyeRegular,
  EyeOffRegular,
} from "@fluentui/react-icons";
import { AppRoutes } from "@core/routing/routes";
import { useAuth } from "@core/auth/useAuth";
import { CambiarClaveLoginDialog } from "@core/auth/CambiarClaveLoginDialog";
import { NotificacionesMenu } from "@features/notificaciones/NotificacionesMenu";
import { GerenciaSwitcher } from "@core/ui/GerenciaSwitcher";
import {
  useVerTodasLasGerencias,
  establecerVerTodasLasGerencias,
  useGerenciaActiva,
} from "@core/gerenciaActiva";
import { infoGerencia } from "@constants/gerencias";
import { useLocalAuth } from "@core/auth/LocalAuthProvider";
import { leerModoAcceso } from "@core/auth/credenciales";
import { ROLE_LABELS, type Role } from "@constants/roles";
import { seedIfEmpty } from "@core/data/adapters/dexie/seed";
import { hidratarMatrizDesdeBackend } from "@features/autoridades/matrizPermisos";
import { NavMenu } from "@core/ui/NavMenu";
import { iniciarSync } from "@core/offline/SyncEngine";
import { SyncStatus } from "@/features/offline/SyncStatus";
import { contarSinSincronizar } from "@core/offline/outbox";
import { TOASTER_ID } from "@core/ui/useToast";
import { ConfirmDialogProvider } from "@core/ui/ConfirmDialog";
import { PWAUpdatePrompt } from "@core/ui/PWAUpdatePrompt";
import { useIsMobile } from "@core/ui/responsive";

const BACKEND = import.meta.env.VITE_BACKEND ?? "sharepoint";
// Backends con login local de clave-maestra (sin Azure AD): Dexie (demo) y
// Supabase (piloto compartido). Solo SharePoint usa el login MSAL corporativo.
const USA_LOGIN_LOCAL = BACKEND === "dexie" || BACKEND === "supabase";
const OFFLINE_ON = import.meta.env.VITE_OFFLINE === "1";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <FluentProvider theme={webLightTheme}>
      <QueryClientProvider client={queryClient}>
        <ConfirmDialogProvider>
          <RouterImpl>
            <AppShell />
            <Toaster toasterId={TOASTER_ID} position="top-end" />
            {/* Banner para "nueva versión disponible" / "listo offline". */}
            <PWAUpdatePrompt />
          </RouterImpl>
        </ConfirmDialogProvider>
      </QueryClientProvider>
    </FluentProvider>
  );
}

function AppShell() {
  const { isAuthenticated } = useAuth();

  // Carga datos de prueba en Dexie la primera vez (modo local o supabase).
  // En supabase, además del padrón compartido en la nube, seedIfEmpty puebla
  // los usuarios locales que usa el login de clave-maestra.
  useEffect(() => {
    if (USA_LOGIN_LOCAL) {
      void seedIfEmpty();
    }
    // La matriz de autoridades es COMPARTIDA. Se trae del backend al arrancar
    // para que lo que configure el administrador aplique en todos los equipos
    // y no solo en el navegador donde se hizo el cambio.
    void hidratarMatrizDesdeBackend();
  }, []);

  if (!isAuthenticated) {
    return USA_LOGIN_LOCAL ? <LocalLoginScreen /> : <SharePointLoginScreen />;
  }
  return <AuthenticatedApp />;
}

// ============== Login local (modo Dexie) ==============
function LocalLoginScreen() {
  const { availableUsers, login, padronCargando } = useLocalAuth();
  const [selected, setSelected] = useState<string>("");
  const [password, setPassword] = useState("");
  // Mostrar/ocultar las claves mientras se digitan (a elección del usuario).
  const [verPassword, setVerPassword] = useState(false);
  const [verAdminPassword, setVerAdminPassword] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [targetRole, setTargetRole] = useState<Role | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [cambiarClaveOpen, setCambiarClaveOpen] = useState(false);

  // El campo de usuario arranca VACÍO: el usuario escribe su email (con
  // autocompletado del navegador / gestor de claves). No se prerrellena ni se
  // ofrece la lista completa de usuarios — eso, además de molestar al borrar,
  // exponía el padrón completo en la página de login.

  // Resuelve el usuario seleccionado (si el email tipeado coincide con
  // alguno conocido). Sirve para mostrar el selector de rol cuando aplique.
  const usuarioSel = availableUsers.find(
    (u) => u.email.toLowerCase() === selected.toLowerCase(),
  );
  const rolesDisponibles = usuarioSel?.rolesDisponibles ?? [];
  const tieneMultiplesRoles = rolesDisponibles.length > 1;

  // Al cambiar de usuario, limpiamos error/claves y resetamos el rol al
  // primario del nuevo usuario (si lo encontramos).
  useEffect(() => {
    if (!selected) return;
    setError(null);
    setPassword("");
    setAdminPassword("");
    setTargetRole(usuarioSel?.rolPrimario ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  /**
   * El padrón se carga por red. Si el usuario termina de escribir su correo —o
   * el gestor de claves lo autocompleta— ANTES de que llegue, el efecto de
   * arriba fija `targetRole` en null y, como solo depende del correo, nunca
   * vuelve a correr: la casilla de la segunda clave del administrador jamás
   * aparece y el ingreso se rechaza por enviarla vacía.
   *
   * Este efecto cierra esa carrera: en cuanto el padrón resuelve al usuario,
   * fija su rol por defecto. Solo actúa si aún no hay rol elegido, para no
   * pisar la selección manual del desplegable ni borrar lo ya digitado.
   */
  useEffect(() => {
    if (targetRole === null && usuarioSel) setTargetRole(usuarioSel.rolPrimario);
  }, [usuarioSel, targetRole]);

  const ingresar = async () => {
    if (!selected) return;
    setEnviando(true);
    setError(null);
    try {
      const role = targetRole ?? usuarioSel?.rolPrimario;
      const res = await login(selected, password, {
        ...(role ? { targetRole: role } : {}),
        ...(role === "ADMIN_PMIS" ? { adminPassword } : {}),
      });
      if (!res.ok) {
        setError(res.mensaje ?? "No se pudo iniciar sesión.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error desconocido.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "2.5rem",
        minHeight: "100vh",
        padding: "2rem",
        // Foto aérea del CPF como fondo, LIMPIA (sin velo). El cuadro blanco de
        // ingreso y el logo van encima. `backgroundColor` es solo respaldo por si
        // la imagen no carga (no se ve cuando la foto está presente).
        backgroundColor: "#093C71",
        backgroundImage: `url(${import.meta.env.BASE_URL}login-bg.jpg)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <img
        src={`${import.meta.env.BASE_URL}pmis-icon.svg`}
        alt="PMIS — Dyvale Ingeniería"
        width={168}
        height={168}
        style={{
          borderRadius: 28,
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.30)",
          flexShrink: 0,
        }}
      />
      <div
        style={{
          background: "white",
          borderRadius: 8,
          padding: "2.25rem 2rem",
          width: "100%",
          maxWidth: 420,
          boxShadow: "0 8px 32px rgba(0, 58, 112, 0.25)",
        }}
      >
        <h2 style={{ color: "#003A70", margin: 0, fontWeight: 600 }}>PMIS</h2>
        <Text size={200} style={{ color: "#666", display: "block", marginBottom: "1.5rem" }}>
          Sistema de Información para Gestión de Proyectos
        </Text>

        <label
          htmlFor="login-user"
          style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4, color: "#222" }}
        >
          Usuario
          <em style={{ color: "#999", fontWeight: 400, marginLeft: 6 }}>
            — escriba su email
          </em>
        </label>
        <input
          id="login-user"
          type="email"
          name="email"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          autoComplete="username"
          spellCheck={false}
          placeholder="su.correo@empresa.com"
          onKeyDown={(e) => {
            if (e.key === "Enter") void ingresar();
          }}
          style={{
            width: "100%",
            padding: "0.55rem 0.65rem",
            border: "1px solid #d1d1d1",
            borderRadius: 4,
            fontSize: 14,
            marginBottom: "0.35rem",
            background: "white",
          }}
        />
        <Text
          size={200}
          style={{ color: "#888", display: "block", marginBottom: "1rem" }}
        >
          ¿No recuerda su correo? Consúltelo con el administrador del PMIS.
        </Text>

        {tieneMultiplesRoles && (
          <>
            <label
              htmlFor="login-role"
              style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4, color: "#222" }}
            >
              Ingresar como
              <em style={{ color: "#999", fontWeight: 400, marginLeft: 6 }}>
                — este usuario tiene varios roles
              </em>
            </label>
            <select
              id="login-role"
              value={targetRole ?? ""}
              onChange={(e) => setTargetRole(e.target.value as Role)}
              disabled={enviando}
              style={{
                width: "100%",
                padding: "0.55rem 0.65rem",
                border: "1px solid #d1d1d1",
                borderRadius: 4,
                fontSize: 14,
                marginBottom: "1rem",
                background: "white",
              }}
            >
              {rolesDisponibles.map((r) => (
                <option key={r} value={r}>
                  {r === "ADMIN_PMIS"
                    ? "Administrador del PMIS (requiere segunda clave)"
                    : `${r} — ${ROLE_LABELS[r]}`}
                </option>
              ))}
            </select>
          </>
        )}

        <label
          htmlFor="login-pass"
          style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4, color: "#222" }}
        >
          Contraseña
          <em style={{ color: "#003A70", fontWeight: 400, marginLeft: 6 }}>
            {leerModoAcceso() === "clave-maestra"
              ? "— modo PRUEBAS"
              : "— modo SP-Tenant"}
          </em>
        </label>
        <div style={{ position: "relative", marginBottom: "0.75rem" }}>
          <input
            id="login-pass"
            type={verPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={enviando}
            placeholder="Su clave de acceso"
            autoComplete="current-password"
            onKeyDown={(e) => {
              if (e.key === "Enter") void ingresar();
            }}
            style={{
              width: "100%",
              padding: "0.55rem 2.4rem 0.55rem 0.65rem",
              border: "1px solid #d1d1d1",
              borderRadius: 4,
              fontSize: 14,
              background: "white",
              color: "#222",
              boxSizing: "border-box",
            }}
          />
          <button
            type="button"
            onClick={() => setVerPassword((v) => !v)}
            disabled={enviando}
            aria-label={verPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            title={verPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            style={{
              position: "absolute",
              right: 6,
              top: "50%",
              transform: "translateY(-50%)",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 18,
              padding: "0.2rem",
              lineHeight: 1,
              color: "#003A70",
              display: "flex",
              alignItems: "center",
            }}
          >
            {verPassword ? <EyeOffRegular /> : <EyeRegular />}
          </button>
        </div>

        {targetRole === "ADMIN_PMIS" && (
          <>
            <label
              htmlFor="login-admin-pass"
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 4,
                color: "#312E81",
              }}
            >
              Clave de Administrador
              <em style={{ color: "#999", fontWeight: 400, marginLeft: 6 }}>
                — segunda barrera, solo el admin la conoce
              </em>
            </label>
            <div style={{ position: "relative", marginBottom: "0.75rem" }}>
              <input
                id="login-admin-pass"
                type={verAdminPassword ? "text" : "password"}
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                disabled={enviando}
                placeholder="Clave de administración"
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.key === "Enter") void ingresar();
                }}
                style={{
                  width: "100%",
                  padding: "0.55rem 2.4rem 0.55rem 0.65rem",
                  border: "1px solid #A5B4FC",
                  borderRadius: 4,
                  fontSize: 14,
                  background: "#F5F3FF",
                  color: "#222",
                  boxSizing: "border-box",
                }}
              />
              <button
                type="button"
                onClick={() => setVerAdminPassword((v) => !v)}
                disabled={enviando}
                aria-label={verAdminPassword ? "Ocultar clave" : "Mostrar clave"}
                title={verAdminPassword ? "Ocultar clave" : "Mostrar clave"}
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 18,
                  padding: "0.2rem",
                  lineHeight: 1,
                  color: "#312E81",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {verAdminPassword ? <EyeOffRegular /> : <EyeRegular />}
              </button>
            </div>
          </>
        )}

        {error && (
          <div
            style={{
              padding: "0.5rem 0.75rem",
              background: "#FEE2E2",
              border: "1px solid #FCA5A5",
              borderRadius: 4,
              color: "#991B1B",
              fontSize: 12,
              marginBottom: "0.75rem",
            }}
          >
            {error}
          </div>
        )}

        <button
          onClick={() => void ingresar()}
          // Sin padrón cargado, el ingreso fallaría con "Usuario no encontrado"
          // aunque la clave sea correcta: se espera en vez de rechazar.
          disabled={enviando || padronCargando || !selected.trim()}
          style={{
            width: "100%",
            padding: "0.65rem",
            background: enviando || padronCargando ? "#9CA3AF" : "#0073B4",
            color: "white",
            border: "none",
            borderRadius: 4,
            fontSize: 14,
            fontWeight: 600,
            cursor: enviando || padronCargando ? "wait" : "pointer",
          }}
        >
          {enviando ? "Ingresando…" : padronCargando ? "Cargando usuarios…" : "Ingresar"}
        </button>

        <button
          type="button"
          onClick={() => setCambiarClaveOpen(true)}
          style={{
            background: "none",
            border: "none",
            color: "#0073B4",
            fontSize: 13,
            cursor: "pointer",
            padding: "0.5rem 0 0",
            textDecoration: "underline",
          }}
        >
          Cambiar mi clave
        </button>

        <Text size={100} style={{ color: "#999", display: "block", marginTop: "1rem" }}>
          {availableUsers.length === 0
            ? "Cargando usuarios del PMIS..."
            : `${availableUsers.length} usuarios activos en Autoridades.${
                BACKEND === "supabase" ? " Datos compartidos en la nube." : " Datos locales (IndexedDB)."
              }`}
        </Text>

        <CambiarClaveLoginDialog
          open={cambiarClaveOpen}
          onOpenChange={setCambiarClaveOpen}
          knownEmails={availableUsers.map((u) => u.email.toLowerCase())}
        />
      </div>
    </div>
  );
}

// ============== Login SharePoint (modo MSAL) ==============
function SharePointLoginScreen() {
  const { login } = useAuth();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: "1rem",
      }}
    >
      <h1 style={{ color: "#003A70" }}>PMIS - Divale ING</h1>
      <p>Inicia sesion con tu cuenta corporativa.</p>
      <Button appearance="primary" onClick={() => void login()}>
        Iniciar sesion
      </Button>
    </div>
  );
}

// ============== App autenticada ==============
function AuthenticatedApp() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Arranca el motor de sincronización offline (caché + outbox) cuando la capa
  // offline está activa (VITE_OFFLINE=1) y hay un tenant resuelto.
  useEffect(() => {
    const tid = user?.tenantId;
    if (!OFFLINE_ON || !tid) return;
    return iniciarSync(tid);
  }, [user?.tenantId]);

  if (!user) return <Spinner label="Cargando..." />;

  // En mobile: sin sidebar fijo, el NavMenu se monta como drawer overlay.
  // En desktop: layout clásico con sidebar a la izquierda.
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <div className="pmis-print-hide">
        <Header
          isMobile={isMobile}
          onToggleDrawer={() => setDrawerOpen((o) => !o)}
        />
        <BannerInterGerencia />
      </div>
      <div style={{ display: "flex", flex: 1 }}>
        {!isMobile && (
          <div className="pmis-print-hide">
            <NavMenu mode="sidebar" />
          </div>
        )}
        {isMobile && (
          <NavMenu mode="drawer" open={drawerOpen} onClose={() => setDrawerOpen(false)} />
        )}
        <main
          style={{
            flex: 1,
            padding: isMobile ? "0.5rem" : "1rem",
            // Sin maxWidth en mobile — aprovechar todo el ancho.
            // En desktop mantenemos 1400px para mantener legible.
            ...(isMobile ? {} : { maxWidth: 1400 }),
            width: "100%",
            overflowX: "hidden",
          }}
        >
          <AppRoutes />
        </main>
      </div>
    </div>
  );
}

interface HeaderProps {
  isMobile: boolean;
  onToggleDrawer: () => void;
}

function Header({ isMobile, onToggleDrawer }: HeaderProps) {
  const { user, logout } = useAuth();

  // Cerrar sesión purga los datos locales; si hay cambios sin sincronizar,
  // avisar antes para no perderlos.
  const manejarSalir = async (): Promise<void> => {
    if (OFFLINE_ON && user?.tenantId) {
      const n = await contarSinSincronizar(user.tenantId);
      if (
        n > 0 &&
        !window.confirm(
          `Hay ${n} cambio(s) sin sincronizar que se perderán al cerrar sesión. ¿Cerrar sesión de todas formas?`,
        )
      ) {
        return;
      }
    }
    void logout();
  };
  return (
    <header
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: isMobile ? "0.5rem 0.75rem" : "0.75rem 1.5rem",
        background: "#003A70",
        color: "white",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: "1 1 auto", overflow: "hidden" }}>
        {isMobile && (
          <button
            onClick={onToggleDrawer}
            aria-label="Abrir menú"
            style={{
              background: "transparent",
              border: "none",
              color: "white",
              cursor: "pointer",
              padding: 8,
              display: "flex",
              alignItems: "center",
              minWidth: 44,
              minHeight: 44,
              borderRadius: 4,
            }}
          >
            <NavigationRegular fontSize={22} />
          </button>
        )}
        <strong
          style={{
            fontSize: isMobile ? 14 : 16,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {isMobile ? "PMIS" : "PMIS - Divale ING"}
        </strong>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 6 : "1rem",
          flexShrink: 0,
        }}
      >
        {user && <GerenciaSwitcher isMobile={isMobile} />}
        {user && <NotificacionesMenu />}
        {/*
          Switcher de usuario (modo local: demo y supabase). El ADMIN_PMIS puede
          "ver como" cualquier usuario para verificar fallas y VOLVER a
          Administrador sin cerrar sesión (el switcher decide su UI según el rol
          real). Los usuarios normales solo ven su nombre. En modo SharePoint
          (MSAL) no se muestra: el usuario es el dueño de la sesión SP.
        */}
        {USA_LOGIN_LOCAL ? (
          <UserSwitcher isMobile={isMobile} />
        ) : (
          !isMobile && (
            <span>
              {user?.name} {user?.role && <em style={{ opacity: 0.8 }}>({user.role})</em>}
            </span>
          )
        )}
        <SyncStatus />
        <Button
          appearance="subtle"
          onClick={() => void manejarSalir()}
          style={{ color: "white", minWidth: isMobile ? 44 : undefined }}
          size={isMobile ? "small" : "medium"}
        >
          {isMobile ? "Salir" : "Cerrar sesión"}
        </Button>
      </div>
    </header>
  );
}

/**
 * Menu para cambiar rápidamente de usuario en modo Dexie (desarrollo).
 * Permite probar permisos por rol sin tener que cerrar sesión y volver a
 * entrar en cada cambio.
 */
function UserSwitcher({ isMobile = false }: { isMobile?: boolean }) {
  const { user } = useAuth();
  const { availableUsers, loginAs, adminReal } = useLocalAuth();
  const impersonando = !!adminReal;
  // Solo el ADMIN real (o cuando está impersonando) ve el switcher. Los demás
  // usuarios en modo local ven únicamente su nombre.
  const puedeCambiar = user?.role === "ADMIN_PMIS" || impersonando;
  if (!puedeCambiar) {
    return !isMobile ? (
      <span>
        {user?.name}{" "}
        {user?.role && <em style={{ opacity: 0.8 }}>({ROLE_LABELS[user.role]})</em>}
      </span>
    ) : null;
  }
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          icon={<PeopleSwapRegular />}
          iconPosition="before"
          style={{ color: "white", minWidth: isMobile ? 44 : undefined }}
          size={isMobile ? "small" : "medium"}
        >
          {!isMobile && impersonando && (
            <span
              style={{
                background: "#F0A500",
                color: "#3a2a00",
                fontSize: 10,
                fontWeight: 700,
                padding: "1px 6px",
                borderRadius: 8,
                marginRight: 6,
              }}
            >
              VIENDO COMO
            </span>
          )}
          {!isMobile && user?.name}
          {!isMobile && user?.role && (
            <em style={{ opacity: 0.8, marginLeft: 4 }}>({ROLE_LABELS[user.role]})</em>
          )}
          {!isMobile && <ChevronDownRegular style={{ marginLeft: 6 }} />}
        </Button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {impersonando && adminReal && (
            <>
              <MenuItem
                onClick={() => loginAs(adminReal.email)}
                style={{ fontWeight: 700, color: "#003A70" }}
              >
                ↩ Volver a Administrador ({adminReal.name})
              </MenuItem>
              <MenuDivider />
            </>
          )}
          <div style={{ padding: "0.4rem 0.6rem", fontSize: 11, color: "#666" }}>
            {impersonando ? "Ver como otro usuario:" : "Cambiar a otro usuario:"}
          </div>
          <MenuDivider />
          {availableUsers.map((u) => {
            const activo = user?.email === u.email;
            return (
              <MenuItem
                key={u.email}
                onClick={() => loginAs(u.email)}
                disabled={activo}
                style={{
                  fontWeight: activo ? 700 : 400,
                  background: activo ? "#f0f5fb" : undefined,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span>
                    {u.name}
                    {activo && <span style={{ marginLeft: 6, color: "#2A954F" }}>● actual</span>}
                  </span>
                  <span style={{ fontSize: 11, color: "#666" }}>
                    {ROLE_LABELS[u.role]} · {u.email}
                  </span>
                </div>
              </MenuItem>
            );
          })}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

/**
 * Banner global cuando el usuario está en modo INTER-GERENCIA (vista cross-gerencia
 * de solo lectura). Aparece bajo el Header en toda la app para recordar al
 * usuario que está viendo datos de todos las gerencias y la edición está
 * restringida a su gerencia activa.
 */
function BannerInterGerencia() {
  const verTodos = useVerTodasLasGerencias();
  const gerenciaActiva = useGerenciaActiva();
  if (!verTodos) return null;
  const info = infoGerencia(gerenciaActiva);
  return (
    <div
      style={{
        background: "#FEF3C7",
        borderBottom: "1px solid #F0D78C",
        color: "#92400E",
        padding: "6px 16px",
        fontSize: 12,
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontSize: 16 }}>🌎</span>
      <span style={{ flex: 1 }}>
        <strong>Vista inter-gerencia (solo lectura)</strong> — viendo datos de
        TODOS las gerencias. Para crear o editar, vuelve a tu gerencia activa (
        {info.flag} {info.nombre}). La IA opera global siempre, sin importar
        este filtro.
      </span>
      <button
        type="button"
        onClick={() => establecerVerTodasLasGerencias(false)}
        style={{
          background: "transparent",
          border: "1px solid #92400E",
          color: "#92400E",
          borderRadius: 4,
          padding: "2px 8px",
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Volver a {info.flag} {info.nombre}
      </button>
    </div>
  );
}
